import {
  deleteDataConnectionEnrichment,
  uploadDataConnectionEnrichment,
} from "~/lib/network/database-connection";

export interface FileAttachment {
  fileId: string;
  description: string;
  isEditing: boolean;
  file?: File;
  fileName: string;
  isUploading: boolean;
  isUploadError: boolean;
  isExisting?: boolean;
}

export type ConnectionAttachmentsState = {
  attachments: FileAttachment[];
  newAttachments: FileAttachment[]; // Track newly uploaded files in edit modal
  pendingDelete: FileAttachment | null;
  isDeleting: boolean;

  setAttachments: (attachments: FileAttachment[]) => void;
  editAttachment: (index: number, updates: Partial<FileAttachment>) => void;
  addAttachments: (files: FileList | File[]) => Promise<void>;
  retryUpload: (fileId: string) => Promise<void>;
  initiateDelete: (attachmentToDelete: FileAttachment) => void;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
  deleteAllAttachments: () => Promise<void>;
  deleteNewAttachments: () => Promise<void>; // Delete only newly uploaded files
  reset: () => void;
};

const defaultState: Omit<
  ConnectionAttachmentsState,
  | "setAttachments"
  | "editAttachment"
  | "addAttachments"
  | "retryUpload"
  | "initiateDelete"
  | "confirmDelete"
  | "cancelDelete"
  | "deleteAllAttachments"
  | "deleteNewAttachments"
  | "reset"
> = {
  attachments: [],
  newAttachments: [],
  pendingDelete: null,
  isDeleting: false,
};

export const createConnectionAttachmentsSlice = (
  set: any,
  get: any,
  storeApi: any
) => {
  return {
    ...defaultState,

    setAttachments: (attachments: FileAttachment[]) => set({ attachments }),

    editAttachment: (index: number, updates: Partial<FileAttachment>) =>
      set((state: ConnectionAttachmentsState) => ({
        attachments: state.attachments.map((attachment, i) =>
          i === index ? { ...attachment, ...updates } : attachment
        ),
      })),

    addAttachments: async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const { attachments } = get();

      // Filter files by type and avoid duplicates
      const filteredFiles = fileArray.filter(
        (file) =>
          (file.name.toLowerCase().endsWith(".txt") ||
            file.name.toLowerCase().endsWith(".md") ||
            file.name.toLowerCase().endsWith(".docx") ||
            file.name.toLowerCase().endsWith(".pdf")) &&
          !attachments.some(
            (existingFile: FileAttachment) =>
              existingFile.fileName === file.name
          )
      );

      // Create new attachments with uploading state
      const newAttachments: FileAttachment[] = filteredFiles.map((file) => ({
        fileId: crypto.randomUUID(),
        fileName: file.name,
        description: "",
        isEditing: false,
        file: file,
        isUploading: true,
        isUploadError: false,
        isExisting: false,
      }));

      // Check file limits
      const filesToAdd = newAttachments;

      // Add files to both attachments and newAttachments arrays
      set((state: ConnectionAttachmentsState) => ({
        attachments: [...state.attachments, ...filesToAdd],
        newAttachments: [...state.newAttachments, ...filesToAdd],
      }));

      // Upload files
      const uploadPromises = filesToAdd.map(async (attachment) => {
        try {
          if (!attachment.file) {
            throw new Error("File is required for upload");
          }
          const response = await uploadDataConnectionEnrichment(
            attachment.fileId,
            attachment.file
          );
          return { attachmentId: attachment.fileId, success: response.success };
        } catch (error) {
          console.error(`Upload failed for ${attachment.fileName}:`, error);
          return { attachmentId: attachment.fileId, success: false };
        }
      });

      // Process upload results
      try {
        const results = await Promise.allSettled(uploadPromises);
        const { attachments: currentAttachments } = get();

        const updatedAttachments = currentAttachments.map(
          (item: FileAttachment) => {
            const uploadResult = results.find(
              (result) =>
                result.status === "fulfilled" &&
                result.value.attachmentId === item.fileId
            );

            if (uploadResult && uploadResult.status === "fulfilled") {
              return {
                ...item,
                isUploading: false,
                isUploadError: !uploadResult.value.success,
              };
            }

            return item;
          }
        );

        set({ attachments: updatedAttachments });
      } catch (error) {
        console.error("Error processing upload results:", error);
      }
    },

    retryUpload: async (fileId: string) => {
      const { attachments } = get();

      // Set uploading state
      const updatedAttachments = attachments.map((attachment: FileAttachment) =>
        attachment.fileId === fileId
          ? { ...attachment, isUploading: true, isUploadError: false }
          : attachment
      );
      set({ attachments: updatedAttachments });

      // Find the attachment to retry
      const attachmentToRetry = updatedAttachments.find(
        (attachment: FileAttachment) => attachment.fileId === fileId
      );

      if (attachmentToRetry && attachmentToRetry.file) {
        try {
          const response = await uploadDataConnectionEnrichment(
            attachmentToRetry.fileId,
            attachmentToRetry.file
          );

          // Update the attachment based on upload result
          const { attachments: currentAttachments } = get();
          const finalAttachments = currentAttachments.map(
            (attachment: FileAttachment) =>
              attachment.fileId === fileId
                ? {
                    ...attachment,
                    isUploading: false,
                    isUploadError: !response.success,
                  }
                : attachment
          );
          set({ attachments: finalAttachments });
        } catch (error) {
          console.error(`Retry upload failed for fileId ${fileId}:`, error);
          // Handle upload error
          const { attachments: currentAttachments } = get();
          const errorAttachments = currentAttachments.map(
            (attachment: FileAttachment) =>
              attachment.fileId === fileId
                ? { ...attachment, isUploading: false, isUploadError: true }
                : attachment
          );
          set({ attachments: errorAttachments });
        }
      }
    },

    initiateDelete: (attachmentToDelete: FileAttachment) => {
      set({ pendingDelete: attachmentToDelete });
    },

    confirmDelete: async () => {
      const { pendingDelete } = get();
      if (!pendingDelete) return;

      set({ isDeleting: true });

      try {
        // Get connection ID from database slice
        const { connectionDetails } = storeApi.getState();
        const connectionId = connectionDetails?._id;

        // Delete from backend with connection ID for reference checking
        await deleteDataConnectionEnrichment(
          [
            {
              file_id: pendingDelete.fileId,
              file_name: pendingDelete.fileName,
            },
          ],
          connectionId
        );

        // Remove from UI state after successful backend deletion
        set((state: ConnectionAttachmentsState) => ({
          attachments: state.attachments.filter(
            (attachment: FileAttachment) =>
              attachment.fileId !== pendingDelete.fileId
          ),
          newAttachments: state.newAttachments.filter(
            (attachment: FileAttachment) =>
              attachment.fileId !== pendingDelete.fileId
          ),
          pendingDelete: null,
          isDeleting: false,
        }));
      } catch (error) {
        console.error("Failed to delete attachment:", error);
        // Clear pending delete but keep the file, show error
        set({ pendingDelete: null, isDeleting: false });
        alert("Failed to delete file. Please try again.");
        throw error;
      }
    },

    cancelDelete: () => {
      set({ pendingDelete: null, isDeleting: false });
    },

    deleteAllAttachments: async () => {
      const { attachments } = get();

      if (attachments.length === 0) {
        return;
      }

      try {
        // Get connection ID from database slice
        const { connectionDetails } = storeApi.getState();
        const connectionId = connectionDetails?._id;

        // Convert to the format expected by the API
        const filesToDelete = attachments.map((attachment: FileAttachment) => ({
          file_id: attachment.fileId,
          file_name: attachment.fileName,
        }));

        // Delete all files from backend with connection ID for reference checking
        await deleteDataConnectionEnrichment(filesToDelete, connectionId);

        // Clear all attachments from UI
        set({
          attachments: [],
          newAttachments: [],
          pendingDelete: null,
          isDeleting: false,
        });
      } catch (error) {
        console.error("Failed to delete all attachments:", error);
        throw error;
      }
    },

    deleteNewAttachments: async () => {
      const { newAttachments } = get();

      if (newAttachments.length === 0) {
        return;
      }

      try {
        // Convert to the format expected by the API
        const filesToDelete = newAttachments.map(
          (attachment: FileAttachment) => ({
            file_id: attachment.fileId,
            file_name: attachment.fileName,
          })
        );

        // Delete only new attachments from backend (no connection_id = delete without reference checking)
        await deleteDataConnectionEnrichment(filesToDelete);

        // Remove new attachments from both arrays
        const newAttachmentIds = newAttachments.map(
          (a: FileAttachment) => a.fileId
        );
        set((state: ConnectionAttachmentsState) => ({
          attachments: state.attachments.filter(
            (attachment) => !newAttachmentIds.includes(attachment.fileId)
          ),
          newAttachments: [],
          pendingDelete: null,
          isDeleting: false,
        }));
      } catch (error) {
        console.error("Failed to delete new attachments:", error);
        throw error;
      }
    },

    resetAttachments: () => set(defaultState),
  };
};
